import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Mic, UserCheck, Award, Zap, Heart, Shield, Star, GraduationCap, Building, School, BookOpen, Check, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxpbnRlcm5hdGlvbmFsJTIwc2Nob29sJTIwc3R1ZGVudHMlMjBkaXZlcnNlJTIwY2xhc3Nyb29tJTIwbW9kZXJufGVufDB8fHx8MTc4MDUxOTY5Mnww&ixlib=rb-4.1.0&q=85';

export default function PartnershipsPage() {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({ name: '', school: '', role: '', email: '', message: '', _hp: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData._hp) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
    } catch (err) {
      console.error(err);
    }
    setSubmitted(true);
  };

  return (
    <div data-testid="partnerships-page" className="min-h-screen bg-white">
      <SEO path="/partnerships" />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#14274B] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={HERO_IMAGE} alt="Élèves internationaux en classe — partenariat école bilingue OFA France" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Créez une section américaine dans votre établissement' : 'Create an American section in your school'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/70 max-w-3xl mb-10"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'OFA France accompagne les écoles primaires, collèges et lycées qui souhaitent offrir à leurs élèves un véritable parcours américain accrédité, de la Grande Section à la Terminale.'
              : 'OFA France supports primary schools, middle schools and high schools that wish to offer their students a genuine accredited American pathway, from Kindergarten to 12th grade.'}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a href="#contact-partner">
              <Button size="lg" className="bg-[#2D78F6] text-white hover:bg-white hover:text-[#14274B] px-8 py-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Planifier un échange' : 'Schedule a meeting'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/programs">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Découvrir le programme' : 'Discover the program'}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <QuickLinksBar />

      {/* Section 1 - Simple Solution */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-light text-[#14274B] mb-6"
              style={{ fontFamily: 'Gabarito, sans-serif' }}
            >
              {language === 'fr' ? 'Proposer un parcours américain sans créer une nouvelle école' : 'Offer an American pathway without creating a new school'}
            </motion.h2>
            <div className="space-y-4 text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <p>{language === 'fr'
                ? 'De nombreuses écoles souhaitent répondre aux attentes des familles en matière d\'ouverture internationale, mais ne disposent pas toujours des ressources nécessaires pour développer une section américaine complète.'
                : 'Many schools want to meet families\' expectations for international openness, but don\'t always have the resources to develop a full American section.'}</p>
              <p>{language === 'fr'
                ? 'OFA France permet d\'intégrer un parcours américain structuré au sein de votre établissement tout en conservant votre identité, votre projet éducatif et votre organisation.'
                : 'OFA France allows you to integrate a structured American pathway within your school while preserving your identity, educational project and organization.'}</p>
              <p className="font-medium text-[#14274B]">{language === 'fr'
                ? 'Notre modèle est conçu pour s\'intégrer facilement à la scolarité existante des élèves.'
                : 'Our model is designed to easily integrate with students\' existing schooling.'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 - Two Tracks */}
      <section className="py-16 bg-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-light text-[#14274B] mb-10" style={{ fontFamily: 'Gabarito, sans-serif' }}>
            {language === 'fr' ? 'Rester bilingue ou devenir bilingue' : 'Stay bilingual or become bilingual'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white p-8 border-t-4 border-[#2D78F6]">
              <h3 className="text-xl font-semibold text-[#2D78F6] mb-3" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Rester bilingue' : 'Stay bilingual'}
              </h3>
              <p className="text-sm text-[#475569] italic mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Pour les élèves déjà bilingues ou anglophones.' : 'For students who are already bilingual or English-speaking.'}
              </p>
              <p className="text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Le programme leur permet de maintenir et développer leur anglais académique à travers des cours dispensés entièrement en anglais et une exposition régulière aux méthodes d\'apprentissage américaines.'
                  : 'The program allows them to maintain and develop their academic English through courses delivered entirely in English and regular exposure to American learning methods.'}
              </p>
            </div>
            <div className="bg-white p-8 border-t-4 border-[#66CA9D]">
              <h3 className="text-xl font-semibold text-[#66CA9D] mb-3" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Devenir bilingue' : 'Become bilingual'}
              </h3>
              <p className="text-sm text-[#475569] italic mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Pour les élèves qui construisent progressivement leur bilinguisme.' : 'For students progressively building their bilingualism.'}
              </p>
              <p className="text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Le programme offre une immersion académique progressive qui permet de développer la compréhension, l\'expression orale et la confiance dans l\'utilisation de l\'anglais au quotidien.'
                  : 'The program offers progressive academic immersion that develops comprehension, oral expression and confidence in using English daily.'}
              </p>
            </div>
          </div>
          <p className="text-center text-[#14274B] italic font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Le bilinguisme n\'est pas un niveau à atteindre. C\'est un parcours qui se construit et s\'entretient dans la durée.'
              : 'Bilingualism is not a level to reach. It is a pathway that is built and maintained over time.'}
          </p>
        </div>
      </section>

      {/* Section 3 - Program */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-light text-[#14274B] mb-10" style={{ fontFamily: 'Gabarito, sans-serif' }}>
            {language === 'fr' ? 'Un parcours américain de la Grande Section à la Terminale' : 'An American pathway from Kindergarten to 12th grade'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <ul className="space-y-4">
                {(language === 'fr' ? [
                  'De la Grande Section à la Terminale',
                  'Enseignement entièrement en anglais',
                  '1h30 de cours par semaine',
                  'Deux séances de 45 minutes ou un bloc de 1h30',
                  '48 heures de cours synchrones par an',
                  'Compatible avec la scolarité principale de l\'élève',
                  'Intégration simple dans l\'emploi du temps de l\'école partenaire',
                ] : [
                  'From Kindergarten to 12th grade',
                  'Instruction entirely in English',
                  '1.5 hours of class per week',
                  'Two 45-minute sessions or one 1.5-hour block',
                  '48 hours of synchronous classes per year',
                  'Compatible with the student\'s main schooling',
                  'Simple integration into the partner school\'s timetable',
                ]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#66CA9D] flex-shrink-0 mt-0.5" />
                    <span className="text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Progression Timeline */}
            <div className="bg-[#14274B] p-8">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Progression du parcours' : 'Pathway progression'}
              </p>
              {['Grande Section', 'Élémentaire', 'Collège', 'Lycée', 'High School Diploma'].map((step, i) => (
                <div key={i} className="flex items-center gap-4 mb-4 last:mb-0">
                  <div className={`w-8 h-8 flex items-center justify-center text-sm font-bold text-white ${i === 4 ? 'bg-[#F8CD46]' : 'bg-[#2D78F6]'}`} style={{ fontFamily: 'Gabarito, sans-serif' }}>
                    {i < 4 ? i + 1 : <GraduationCap className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm font-medium ${i === 4 ? 'text-[#F8CD46]' : 'text-white'}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {step}
                  </span>
                  {i < 4 && <ChevronRight className="w-4 h-4 text-white/30 ml-auto" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - What Makes Us Different */}
      <section className="py-16 bg-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-light text-[#14274B] mb-10" style={{ fontFamily: 'Gabarito, sans-serif' }}>
            {language === 'fr' ? 'Une approche centrée sur l\'élève' : 'A student-centered approach'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(language === 'fr' ? [
              { icon: Users, title: 'Petits effectifs', desc: 'Des groupes réduits favorisant la participation active et les échanges réguliers avec l\'enseignant.' },
              { icon: Mic, title: 'Développement de l\'oral', desc: 'L\'expression orale est au coeur de notre approche afin de développer aisance, confiance et fluidité.' },
              { icon: UserCheck, title: 'Suivi personnalisé', desc: 'Chaque élève bénéficie d\'un accompagnement attentif et d\'un suivi adapté à son niveau et à ses objectifs.' },
              { icon: Award, title: 'Enseignants qualifiés', desc: 'Des enseignants expérimentés accompagnent les élèves tout au long de leur parcours.' },
            ] : [
              { icon: Users, title: 'Small class sizes', desc: 'Reduced groups fostering active participation and regular exchanges with the teacher.' },
              { icon: Mic, title: 'Oral development', desc: 'Oral expression is at the heart of our approach to develop ease, confidence and fluency.' },
              { icon: UserCheck, title: 'Personalized support', desc: 'Each student benefits from attentive guidance and support adapted to their level and goals.' },
              { icon: Award, title: 'Qualified teachers', desc: 'Experienced teachers support students throughout their pathway.' },
            ]).map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 * i }}
                className="bg-white p-8"
              >
                <card.icon className="w-10 h-10 text-[#2D78F6] mb-4" />
                <h3 className="text-lg font-semibold text-[#14274B] mb-2" style={{ fontFamily: 'Gabarito, sans-serif' }}>{card.title}</h3>
                <p className="text-sm text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 - Accreditation */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-light text-[#14274B] mb-6" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Un programme américain reconnu' : 'A recognized American program'}
              </h2>
              <div className="space-y-4 text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <p>{language === 'fr'
                  ? 'Le programme est proposé par OFA Lycée, école américaine en ligne accréditée par Cognia.'
                  : 'The program is offered by OFA Lycée, an online American school accredited by Cognia.'}</p>
                <p>{language === 'fr'
                  ? 'Cette accréditation garantit la qualité du programme et la reconnaissance du parcours académique.'
                  : 'This accreditation guarantees program quality and academic pathway recognition.'}</p>
                <p>{language === 'fr'
                  ? 'Au lycée, les élèves peuvent accumuler des crédits américains et, selon leur parcours, préparer l\'obtention du High School Diploma américain.'
                  : 'In high school, students can accumulate American credits and, depending on their track, prepare for the American High School Diploma.'}</p>
              </div>
            </div>
            <div className="bg-[#14274B] p-8">
              {(language === 'fr' ? [
                'Accréditation américaine',
                'Crédits académiques américains',
                'High School Diploma possible',
                'Préparation aux études supérieures internationales',
              ] : [
                'American accreditation',
                'American academic credits',
                'High School Diploma possible',
                'Preparation for international higher education',
              ]).map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-3 border-b border-white/10 last:border-0">
                  <Check className="w-5 h-5 text-[#66CA9D]" />
                  <span className="text-white font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 6 - School Types */}
      <section className="py-16 bg-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-light text-[#14274B] mb-10" style={{ fontFamily: 'Gabarito, sans-serif' }}>
            {language === 'fr' ? 'Des partenariats adaptés à chaque école' : 'Partnerships adapted to each school'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(language === 'fr' ? [
              { icon: BookOpen, title: 'Écoles primaires', desc: 'Introduire une section américaine dès le plus jeune âge.', color: '#66CA9D' },
              { icon: School, title: 'Collèges', desc: 'Renforcer l\'ouverture internationale et préparer les élèves à un parcours bilingue avancé.', color: '#F8CD46' },
              { icon: Building, title: 'Lycées', desc: 'Proposer une véritable section américaine et préparer les élèves au High School Diploma.', color: '#2D78F6' },
            ] : [
              { icon: BookOpen, title: 'Primary schools', desc: 'Introduce an American section from the youngest age.', color: '#66CA9D' },
              { icon: School, title: 'Middle schools', desc: 'Strengthen international openness and prepare students for an advanced bilingual pathway.', color: '#F8CD46' },
              { icon: Building, title: 'High schools', desc: 'Offer a genuine American section and prepare students for the High School Diploma.', color: '#2D78F6' },
            ]).map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 * i }}
                className="bg-white p-8 border-t-4"
                style={{ borderColor: card.color }}
              >
                <card.icon className="w-10 h-10 mb-4" style={{ color: card.color }} />
                <h3 className="text-xl font-semibold text-[#14274B] mb-3" style={{ fontFamily: 'Gabarito, sans-serif' }}>{card.title}</h3>
                <p className="text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 - Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-light text-[#14274B] mb-10" style={{ fontFamily: 'Gabarito, sans-serif' }}>
            {language === 'fr' ? 'Pourquoi devenir partenaire ?' : 'Why become a partner?'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(language === 'fr' ? [
              { icon: Zap, label: 'Mise en oeuvre rapide' },
              { icon: Users, label: 'Aucun recrutement supplémentaire' },
              { icon: Heart, label: 'Accompagnement des familles' },
              { icon: BookOpen, label: 'Programme déjà structuré' },
              { icon: Shield, label: 'Accréditation reconnue' },
              { icon: Star, label: 'Développement du bilinguisme' },
              { icon: Award, label: 'Différenciation de l\'offre éducative' },
              { icon: Building, label: 'Attractivité renforcée pour les familles' },
            ] : [
              { icon: Zap, label: 'Quick implementation' },
              { icon: Users, label: 'No additional recruitment' },
              { icon: Heart, label: 'Family support included' },
              { icon: BookOpen, label: 'Already structured program' },
              { icon: Shield, label: 'Recognized accreditation' },
              { icon: Star, label: 'Bilingualism development' },
              { icon: Award, label: 'Educational offering differentiation' },
              { icon: Building, label: 'Enhanced attractiveness for families' },
            ]).map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.05 * i }}
                className="flex items-center gap-4 p-4 bg-[#E7EAEF]"
              >
                <item.icon className="w-6 h-6 text-[#2D78F6] flex-shrink-0" />
                <span className="text-sm font-medium text-[#14274B]" style={{ fontFamily: 'Poppins, sans-serif' }}>{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Contact Form */}
      <section id="contact-partner" className="py-20 bg-[#14274B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-light text-white mb-6" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Construisons votre section américaine' : 'Let\'s build your American section'}
              </h2>
              <p className="text-white/70 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Chaque établissement est unique.' : 'Every school is unique.'}
              </p>
              <p className="text-white/70" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr'
                  ? 'Nous serons heureux d\'échanger avec vous afin d\'explorer les possibilités de partenariat adaptées à votre projet éducatif et aux besoins de votre communauté scolaire.'
                  : 'We would be happy to discuss with you to explore partnership possibilities adapted to your educational project and the needs of your school community.'}
              </p>
              <div className="mt-8">
                <Link to="/contact">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6 py-5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {language === 'fr' ? 'Nous contacter' : 'Contact us'}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white p-8">
              <h3 className="text-xl font-semibold text-[#14274B] mb-6" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Devenir partenaire' : 'Become a partner'}
              </h3>
              {submitted ? (
                <div className="text-center py-8">
                  <Check className="w-12 h-12 text-[#66CA9D] mx-auto mb-4" />
                  <p className="text-[#14274B] font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {language === 'fr' ? 'Merci ! Nous vous contacterons rapidement.' : 'Thank you! We will contact you shortly.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <input type="text" name="_hp" tabIndex={-1} autoComplete="off" value={formData._hp} onChange={(e) => setFormData({ ...formData, _hp: e.target.value })} />
                  </div>
                  <input
                    type="text"
                    required
                    placeholder={language === 'fr' ? 'Votre nom *' : 'Your name *'}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-[#E7EAEF] text-sm focus:border-[#2D78F6] focus:outline-none"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <input
                    type="text"
                    required
                    placeholder={language === 'fr' ? 'Nom de l\'établissement *' : 'School name *'}
                    value={formData.school}
                    onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                    className="w-full px-4 py-3 border border-[#E7EAEF] text-sm focus:border-[#2D78F6] focus:outline-none"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <input
                    type="text"
                    placeholder={language === 'fr' ? 'Votre fonction' : 'Your role'}
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-3 border border-[#E7EAEF] text-sm focus:border-[#2D78F6] focus:outline-none"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <input
                    type="email"
                    required
                    placeholder={language === 'fr' ? 'Email professionnel *' : 'Professional email *'}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-[#E7EAEF] text-sm focus:border-[#2D78F6] focus:outline-none"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <textarea
                    rows={3}
                    placeholder={language === 'fr' ? 'Votre message (optionnel)' : 'Your message (optional)'}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 border border-[#E7EAEF] text-sm focus:border-[#2D78F6] focus:outline-none resize-none"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Button type="submit" className="w-full bg-[#2D78F6] text-white hover:bg-[#14274B] py-5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {language === 'fr' ? 'Envoyer' : 'Send'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Future section placeholder for partner logos / testimonials */}

      <Footer />
    </div>
  );
}
